import { formatDistanceToNow } from 'date-fns'
import { useTranslations } from 'next-intl'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowRight, faSignalStream } from '@fortawesome/pro-solid-svg-icons'

import type { TVesselJourneyResponse } from '@/api/v1/resources/vesselJourney'
import TeqplayLogo from '@/app/images/teqplayLogo/TeqplayLogo'
import DateClient from '@/app/components/ClientDateFormat/ClientDateFormat'

import './VesselDetailStatus.scss'

// Keep props strictly typed to only what this component needs
export type VesselDetailStatusProps = {
  vessel: TVesselJourneyResponse
  t: ReturnType<typeof useTranslations>
}

export default function VesselDetailStatus({ vessel, t }: VesselDetailStatusProps) {
  // Determine if departure is in the past
  const isDeparted = vessel.departure?.actualTime
    ? new Date(vessel.departure.actualTime).getTime() < Date.now()
    : false

  const hasArrived = vessel.arrival?.actualTime
    ? new Date(vessel.arrival.actualTime).getTime() < Date.now()
    : false

  return (
    <div className="vessel-detail-status">
      <div className="ship-status">
        <div className="status">
          <div className="circle-status" />
          {vessel.status?.replace(/_/g, ' ') || t('common.unknown')}
        </div>
        {vessel.ship.lastUpdated && (
          <div className="last-updated">
            <FontAwesomeIcon icon={faSignalStream} />
            {formatDistanceToNow(new Date(vessel.ship.lastUpdated), { addSuffix: true })}
          </div>
        )}
      </div>

      <div className="ship-departure">
        {!hasArrived && vessel.arrival?.estimatedTime && (
          <div className="item">
            <div className="label">
              {hasArrived ? t('VesselDetails.arrived') : t('VesselDetails.estArrival')}
            </div>
            <div className="value">
              <DateClient date={vessel.arrival.actualTime || vessel.arrival.estimatedTime} />
            </div>
          </div>
        )}
        <div className="item">
          <div className="label">
            {isDeparted ? t('VesselDetails.departed') : t('VesselDetails.departures')}
          </div>
          <div className="value">
            <DateClient date={vessel.departure?.actualTime} />
          </div>
        </div>
      </div>

      <div className="ship-history">
        <div className="ship-location-from">
          {vessel.departure?.port?.name || t('common.unknown')}
        </div>
        <FontAwesomeIcon icon={faArrowRight} className="ship-arrow" />
        <div className="ship-location-to">{vessel.arrival?.port?.name || t('common.unknown')}</div>
      </div>
    </div>
  )
}
